<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { MediaListQuery } from "@apps/iloom_customization/design_feed/queries/MediaListQuery";
    import { DesignFeedService } from "@apps/iloom_customization/design_feed/services/DesignFeedService";
    import { designFeedModalToken } from "@apps/iloom_customization/tokens";
    import { hasNextPage } from "@apps/instagram/utils/paginationUtils";
    import cn from "classnames";
    import { lastValueFrom } from "rxjs";
    import { map } from "rxjs/operators";

    import Masonry from "@components/Masonry/Masonry.svelte";
    import MasonryController from "@components/Masonry/MasonryController";
    import MasonryItem from "@components/Masonry/MasonryItem.svelte";
    import { ModalManager } from "@components/Modal/ModalManager";
    import Spinner from "@components/Spinner/Spinner.svelte";

    import AddMediaToWishlistButton from "./DesignFeedModal/AddMediaToWishlistButton.svelte";

    export let designFeedService: DesignFeedService;
    export let mediaListQuery: MediaListQuery;
    export let modalManager: ModalManager;
    export let withLoadMore = true;

    const masonryController = new MasonryController();

    const mediaList$ = mediaListQuery.mediaList$;
    const mediaListMeta$ = mediaListQuery.meta$;
    const mediaListStatus$ = mediaListQuery.status$;

    const hasNextPage$ = mediaListMeta$.pipe(
        map((meta) => {
            return hasNextPage(meta.limit, meta.offset, meta.total);
        })
    );

    const handleOpenModal = (id: string) => {
        modalManager.openModal(designFeedModalToken, undefined, {
            id,
        });
    };

    const handleAddFaveMedia = (mediaId: string) => {
        lastValueFrom(designFeedService.addFave(mediaId));
    };

    const handleRemoveFaveMedia = (mediaId: string) => {
        lastValueFrom(designFeedService.removeFave(mediaId));
    };

    let loadedImgCount = 0;

    const resetLoadedImgCount = () => {
        loadedImgCount = 0;
    };

    $: loadedImgCount >= $mediaList$.length && masonryController.update();
    $: $mediaListStatus$ === STATUS.LOADING && resetLoadedImgCount();
</script>

{#if $mediaListStatus$ === STATUS.LOADING}
    <div class="gw-df-page-spinner"><Spinner /></div>
{/if}

{#if $mediaListStatus$ === STATUS.READY}
    <div class="gw-df-page-masonry__wrapper">
        <Masonry controller={masonryController}>
            {#each $mediaList$ as item, index}
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <MasonryItem key={index}>
                    <img
                        class="gw-df-page-masonry__image"
                        src={item.src}
                        alt=""
                        on:load={() => {
                            loadedImgCount++;
                            masonryController.update();
                        }}
                        on:click={() => handleOpenModal(item.mediaId)}
                    />
                    <AddMediaToWishlistButton
                        class={cn("gw-df-page-masonry__wishlist-btn", {
                            "gw-df-page-masonry__wishlist-btn--loaded":
                                loadedImgCount >= $mediaList$.length,
                        })}
                        inWishlist={!!item.didViewerFave}
                        handleAdd={() => handleAddFaveMedia(item.mediaId)}
                        handleDelete={() => handleRemoveFaveMedia(item.mediaId)}
                    />
                </MasonryItem>
            {/each}

            {#if $hasNextPage$ && withLoadMore}
                <div class="gw-df-page-masonry__backdrop" />
            {/if}
        </Masonry>
    </div>
{/if}

<style lang="postcss">
    :global(.gw-btn.gw-media-wishlist-btn.gw-df-page-masonry__wishlist-btn) {
        opacity: 0;
    }

    :global(
            .gw-btn.gw-media-wishlist-btn.gw-df-page-masonry__wishlist-btn.gw-df-page-masonry__wishlist-btn--loaded
        ) {
        opacity: 1;
    }
    .gw-df-page-spinner {
        display: flex;
        justify-content: center;
        padding: 2rem;
    }

    .gw-df-page-masonry {
        &__wrapper {
            margin-bottom: 20px;
            position: relative;
            width: 100%;

            :global(.gw-masonry__grid) {
                --gw-masonry-columns-count: 3;
                --gw-masonry-columns-width: 1fr;
            }
        }

        &__image {
            width: 100%;
            object-fit: cover;
            border-radius: 10px;
            cursor: pointer;
        }

        &__backdrop {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 10vh;
            @supports (height: 10dvh) {
                height: 10dvh;
            }
            background: linear-gradient(transparent, #fff);
            pointer-events: none;
        }
    }

    @media all and (max-width: 600px) {
        :global(
                .gw-btn.gw-media-wishlist-btn.gw-df-page-masonry__wishlist-btn
            ) {
            right: 10px;
            bottom: 10px;
        }

        .gw-df-page-masonry {
            &__wrapper {
                :global(.gw-masonry__grid) {
                    --gw-masonry-columns-count: 2;
                }
            }
        }
    }
</style>
